import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { ROUTES } from '@/shared/constants/routes'
import { useAuthStore } from '@/store/authStore'
import { hasPermissionOrAllowLegacy } from '@/shared/utils/permissions'
import PrivateRoute from './PrivateRoute'
import RouteFallback from '@/components/RouteFallback'

// Páginas con carga diferida (code-splitting): cada ruta se sirve en su propio chunk, bajando el
// bundle inicial. El <Suspense> de abajo muestra un fallback mientras se descarga el chunk.
const LoginPage = lazy(() => import('@/modules/auth/pages/LoginPage'))
const ForgotPasswordPage = lazy(() => import('@/modules/auth/pages/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('@/modules/auth/pages/ResetPasswordPage'))
const DashboardPage = lazy(() => import('@/modules/reports/pages/DashboardPage'))
const StudentsPage = lazy(() => import('@/modules/students/pages/StudentsPage'))
const StudentPortalPage = lazy(() => import('@/modules/students/pages/StudentPortalPage'))
const ParentPortalPage = lazy(() => import('@/modules/parents/pages/ParentPortalPage'))
const TutorPortalPage = lazy(() => import('@/modules/tutors/pages/TutorPortalPage'))
const TutorStudentPlanPage = lazy(() => import('@/modules/tutors/pages/TutorStudentPlanPage'))
const ParentAttendancePage = lazy(() => import('@/modules/parents/pages/ParentAttendancePage'))
const ParentTutorPlanPage = lazy(() => import('@/modules/parents/pages/ParentTutorPlanPage'))
const StudentLearningPlanPage = lazy(() => import('@/modules/students/pages/StudentLearningPlanPage'))
const StudentAttendancePage = lazy(() => import('@/modules/students/pages/StudentAttendancePage'))
const StudentAnnouncementsPage = lazy(() => import('@/modules/students/pages/StudentAnnouncementsPage'))
const TeachersPage = lazy(() => import('@/modules/users/pages/TeachersPage'))
const TutorsPage = lazy(() => import('@/modules/users/pages/TutorsPage'))
const CoursesPage = lazy(() => import('@/modules/academic/pages/CoursesPage'))
const TeacherPortalPage = lazy(() => import('@/modules/academic/pages/TeacherPortalPage'))
const PeriodsPage = lazy(() => import('@/modules/academic/pages/PeriodsPage'))
const EnrollmentsPage = lazy(() => import('@/modules/enrollment/pages/EnrollmentsPage'))
const GradesPage = lazy(() => import('@/modules/grades/pages/GradesPage'))
const AttendancePage = lazy(() => import('@/modules/attendance/pages/AttendancePage'))
const AttendanceQrCheckInPage = lazy(() => import('@/modules/attendance/pages/AttendanceQrCheckInPage'))
const ClassroomsPage = lazy(() => import('@/modules/classrooms/pages/ClassroomsPage'))
const LogisticsPage = lazy(() => import('@/modules/logistics/pages/LogisticsPage'))
const FollowUpPage = lazy(() => import('@/modules/followup/pages/FollowUpPage'))
const FollowUpQuestionsPage = lazy(() => import('@/modules/followup/pages/FollowUpQuestionsPage'))
const FollowUpQuestionsCarouselPage = lazy(() => import('@/modules/followup/pages/FollowUpQuestionsCarouselPage'))
const FollowUpBatchesPage = lazy(() => import('@/modules/followup/pages/FollowUpBatchesPage'))
const FollowUpResponsesPage = lazy(() => import('@/modules/followup/pages/FollowUpResponsesPage'))
const NotificationsPage = lazy(() => import('@/modules/notifications/pages/NotificationsPage'))
const AnnouncementsPage = lazy(() => import('@/modules/notifications/pages/AnnouncementsPage'))
const UsersPage = lazy(() => import('@/modules/users/pages/UsersPage'))
const SuperAdminSystemPage = lazy(() => import('@/modules/system/pages/SuperAdminSystemPage'))
const InitialSetupPage = lazy(() => import('@/modules/initial-setup/pages/InitialSetupPage'))
const InitialSetupInstitutionPage = lazy(() => import('@/modules/initial-setup/pages/InitialSetupInstitutionPage'))
const InitialSetupStudyPlanPage = lazy(() => import('@/modules/initial-setup/pages/InitialSetupStudyPlanPage'))
const InitialSetupSubjectsPage = lazy(() => import('@/modules/initial-setup/pages/InitialSetupSubjectsPage'))
const InitialSetupCorrelativitiesPage = lazy(() => import('@/modules/initial-setup/pages/InitialSetupCorrelativitiesPage'))
const StudyPlansPage = lazy(() => import('@/modules/study-plans/pages/StudyPlansPage'))
const CareerDetailPage = lazy(() => import('@/modules/study-plans/pages/CareerDetailPage'))
const GradeScalesPage = lazy(() => import('@/modules/grade-scales/pages/GradeScalesPage'))
const ActaBooksPage = lazy(() => import('@/modules/acta-books/pages/ActaBooksPage'))
const ActaBookDetailPage = lazy(() => import('@/modules/acta-books/pages/ActaBookDetailPage'))
const ElBichoPage = lazy(() => import('@/modules/el-bicho/pages/ElBichoPage'))

function PermissionRoute({ children, permission }: { children: React.ReactNode; permission: string }) {
  const { permissions, role } = useAuthStore()
  const location = useLocation()
  if (!hasPermissionOrAllowLegacy(permissions, permission, role)) {
    return <Navigate to={ROUTES.DASHBOARD} state={{ from: location }} replace />
  }
  return <>{children}</>
}

export default function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      <Route path={ROUTES.LOGIN} element={<LoginPage />} />
      <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
      <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />
      <Route element={<PrivateRoute />}>
        <Route path={ROUTES.DASHBOARD} element={<DashboardPage />} />
        <Route
          path={ROUTES.TEACHER_PORTAL}
          element={
            <PermissionRoute permission="courses.access">
              <TeacherPortalPage />
            </PermissionRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT_PORTAL}
          element={
            <PermissionRoute permission="grades.access">
              <StudentPortalPage />
            </PermissionRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT_LEARNING_PLAN}
          element={
            <PermissionRoute permission="grades.access">
              <StudentLearningPlanPage />
            </PermissionRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT_ATTENDANCE}
          element={
            <PermissionRoute permission="attendance.access">
              <StudentAttendancePage />
            </PermissionRoute>
          }
        />
        <Route
          path={ROUTES.STUDENT_ANNOUNCEMENTS}
          element={
            <PermissionRoute permission="notifications.access">
              <StudentAnnouncementsPage />
            </PermissionRoute>
          }
        />
        <Route
          path={ROUTES.TUTOR_PORTAL}
          element={
            <PermissionRoute permission="students.access">
              <TutorPortalPage />
            </PermissionRoute>
          }
        />
        <Route
          path={ROUTES.TUTOR_STUDENT_PLAN}
          element={
            <PermissionRoute permission="students.access">
              <TutorStudentPlanPage />
            </PermissionRoute>
          }
        />
        <Route
          path={ROUTES.PARENT_PORTAL}
          element={
            <PermissionRoute permission="grades.access">
              <ParentPortalPage />
            </PermissionRoute>
          }
        />
        <Route
          path={ROUTES.PARENT_ATTENDANCE}
          element={
            <PermissionRoute permission="attendance.access">
              <ParentAttendancePage />
            </PermissionRoute>
          }
        />
        <Route
          path={ROUTES.PARENT_TUTOR_PLAN}
          element={
            <PermissionRoute permission="grades.access">
              <ParentTutorPlanPage />
            </PermissionRoute>
          }
        />
        <Route
          path={ROUTES.INITIAL_SETUP}
          element={
            <PermissionRoute permission="initialSetup.access">
              <InitialSetupPage />
            </PermissionRoute>
          }
        />
        <Route
          path={ROUTES.INITIAL_SETUP_INSTITUTION}
          element={
            <PermissionRoute permission="initialSetup.access">
              <InitialSetupInstitutionPage />
            </PermissionRoute>
          }
        />
        <Route
          path={ROUTES.INITIAL_SETUP_STUDY_PLAN}
          element={
            <PermissionRoute permission="initialSetup.access">
              <InitialSetupStudyPlanPage />
            </PermissionRoute>
          }
        />
        <Route
          path={ROUTES.INITIAL_SETUP_SUBJECTS}
          element={
            <PermissionRoute permission="initialSetup.access">
              <InitialSetupSubjectsPage />
            </PermissionRoute>
          }
        />
        <Route
          path={ROUTES.INITIAL_SETUP_CORRELATIVITIES}
          element={
            <PermissionRoute permission="initialSetup.access">
              <InitialSetupCorrelativitiesPage />
            </PermissionRoute>
          }
        />
        <Route
          path={ROUTES.USERS}
          element={
            <PermissionRoute permission="users.access">
              <UsersPage />
            </PermissionRoute>
          }
        />
        <Route path={ROUTES.NOTIFICATIONS} element={<PermissionRoute permission="notifications.access"><NotificationsPage /></PermissionRoute>} />
        <Route path={ROUTES.ANNOUNCEMENTS} element={<PermissionRoute permission="notifications.access"><AnnouncementsPage /></PermissionRoute>} />
        <Route path={ROUTES.STUDENTS} element={<PermissionRoute permission="students.access"><StudentsPage /></PermissionRoute>} />
        <Route path={ROUTES.TEACHERS} element={<PermissionRoute permission="teachers.access"><TeachersPage /></PermissionRoute>} />
        <Route path={ROUTES.TUTORS} element={<PermissionRoute permission="tutors.access"><TutorsPage /></PermissionRoute>} />
        <Route
          path={ROUTES.STUDY_PLANS}
          element={
            <PermissionRoute permission="studyPlans.access">
              <StudyPlansPage />
            </PermissionRoute>
          }
        />
        <Route
          path={`${ROUTES.STUDY_PLAN_CAREER}/:careerId`}
          element={
            <PermissionRoute permission="studyPlans.access">
              <CareerDetailPage />
            </PermissionRoute>
          }
        />
        <Route
          path={ROUTES.GRADE_SCALES}
          element={
            <PermissionRoute permission="gradeScales.access">
              <GradeScalesPage />
            </PermissionRoute>
          }
        />
        <Route
          path={ROUTES.ACTA_BOOKS}
          element={
            <PermissionRoute permission="actaBooks.access">
              <ActaBooksPage />
            </PermissionRoute>
          }
        />
        <Route
          path={`${ROUTES.ACTA_BOOK_DETAIL}/:bookId`}
          element={
            <PermissionRoute permission="actaBooks.access">
              <ActaBookDetailPage />
            </PermissionRoute>
          }
        />
        <Route
          path={ROUTES.EL_BICHO}
          element={
            <PermissionRoute permission="elBicho.access">
              <ElBichoPage />
            </PermissionRoute>
          }
        />
        <Route path={ROUTES.COURSES} element={<PermissionRoute permission="courses.access"><CoursesPage /></PermissionRoute>} />
        <Route path={ROUTES.PERIODS} element={<PermissionRoute permission="periods.access"><PeriodsPage /></PermissionRoute>} />
        <Route path={ROUTES.ENROLLMENTS} element={<PermissionRoute permission="enrollments.access"><EnrollmentsPage /></PermissionRoute>} />
        <Route path={ROUTES.GRADES} element={<PermissionRoute permission="grades.access"><GradesPage /></PermissionRoute>} />
        <Route path={ROUTES.ATTENDANCE} element={<PermissionRoute permission="attendance.access"><AttendancePage /></PermissionRoute>} />
        <Route path={ROUTES.ATTENDANCE_QR_CHECKIN} element={<AttendanceQrCheckInPage />} />
        <Route path={ROUTES.CLASSROOMS} element={<PermissionRoute permission="classrooms.access"><ClassroomsPage /></PermissionRoute>} />
        <Route path={ROUTES.LOGISTICS} element={<PermissionRoute permission="logistics.access"><LogisticsPage /></PermissionRoute>} />
        <Route path={ROUTES.FOLLOW_UP} element={<PermissionRoute permission="followup.access"><FollowUpPage /></PermissionRoute>} />
        <Route
          path={ROUTES.FOLLOW_UP_BATCH_QUESTIONS}
          element={<PermissionRoute permission="followup.access"><FollowUpQuestionsCarouselPage /></PermissionRoute>}
        />
        <Route path={ROUTES.FOLLOW_UP_QUESTIONS} element={<PermissionRoute permission="followup.access"><FollowUpQuestionsPage /></PermissionRoute>} />
        <Route
          path={ROUTES.FOLLOW_UP_BATCHES}
          element={
            <PermissionRoute permission="followup.access">
              <FollowUpBatchesPage />
            </PermissionRoute>
          }
        />
        <Route
          path={ROUTES.FOLLOW_UP_RESPONSES}
          element={
            <PermissionRoute permission="followup.access">
              <FollowUpResponsesPage />
            </PermissionRoute>
          }
        />
        <Route
          path={ROUTES.SUPER_ADMIN_SYSTEM}
          element={
            <PermissionRoute permission="roles.manage">
              <SuperAdminSystemPage />
            </PermissionRoute>
          }
        />
      </Route>
      <Route path={ROUTES.HOME} element={<Navigate to={ROUTES.DASHBOARD} replace />} />
      <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
    </Routes>
    </Suspense>
  )
}
